<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SearchableSelectMenu from '@/components/ui/searchable-select-menu/SearchableSelectMenu.vue';
import TaskGroupingControl from '@/components/tasks/TaskGroupingControl.vue';
import TaskOptionMenu from '@/components/tasks/TaskOptionMenu.vue';
import type { TaskPriority, TenantTaskStatusOption } from '~/types/models';

type TaskViewMode = 'table' | 'kanban';

interface TaskFilterValues {
  status: string;
  priority: string;
  assigneeId: string;
  projectId: string;
  clientId: string;
  tag: string;
}

interface SelectOption {
  value: string;
  label: string;
}

const props = defineProps<{
  viewMode: TaskViewMode;
  groupBy: string;
  groupOptions: SelectOption[];
  taskCount: number | string;
  searchQuery: string;
  filters: TaskFilterValues;
  hasActiveFilters: boolean;
  statusOptions: TenantTaskStatusOption[];
  priorityOptions: Array<{ value: TaskPriority; label: string; color?: string }>;
  assigneeOptions: SelectOption[];
  projectOptions: SelectOption[];
  clientOptions: SelectOption[];
  tagOptions: SelectOption[];
}>();

const emit = defineEmits<{
  (event: 'update:view-mode', value: TaskViewMode): void;
  (event: 'update:group-by', value: string): void;
  (event: 'update:search-query', value: string): void;
  (event: 'update:filter', field: keyof TaskFilterValues, value: string): void;
  (event: 'clear-filters'): void;
}>();
</script>

<template>
  <div class="space-y-4">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex flex-wrap items-center gap-2">
        <TaskGroupingControl
          :model-value="groupBy"
          :options="groupOptions"
          :task-count="taskCount"
          storage-key="tasks-group-by"
          @update:model-value="value => emit('update:group-by', value)"
        />
      </div>

      <div class="inline-flex rounded-full border border-border/70 bg-card/88 p-1 shadow-sm">
        <Button
          :variant="viewMode === 'table' ? 'secondary' : 'ghost'"
          size="sm"
          class="rounded-full"
          @click="emit('update:view-mode', 'table')"
        >
          Table View
        </Button>
        <Button
          :variant="viewMode === 'kanban' ? 'secondary' : 'ghost'"
          size="sm"
          class="rounded-full"
          @click="emit('update:view-mode', 'kanban')"
        >
          Kanban View
        </Button>
      </div>
    </div>

    <div class="filter-shell space-y-4">
      <div class="flex gap-2">
        <Input
          :model-value="searchQuery"
          type="search"
          placeholder="Search tasks or tags..."
          class="flex-1"
          @update:model-value="value => emit('update:search-query', String(value || ''))"
        />
        <Button
          v-if="hasActiveFilters"
          variant="outline"
          @click="emit('clear-filters')"
        >
          Clear Filters
        </Button>
      </div>

      <div class="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <div>
          <label class="mb-1 block text-xs text-muted-foreground">Status</label>
          <TaskOptionMenu
            :model-value="filters.status"
            type="status"
            :options="statusOptions"
            allow-clear
            clear-label="All Statuses"
            variant="form"
            @update:model-value="value => emit('update:filter', 'status', value)"
          >
            <template #trigger="{ option }">
              <span class="text-sm" :class="option ? 'text-foreground' : 'text-muted-foreground'">
                {{ option?.label || 'All Statuses' }}
              </span>
            </template>
          </TaskOptionMenu>
        </div>

        <div>
          <label class="mb-1 block text-xs text-muted-foreground">Priority</label>
          <TaskOptionMenu
            :model-value="filters.priority"
            type="priority"
            :options="priorityOptions"
            allow-clear
            clear-label="All Priorities"
            variant="form"
            @update:model-value="value => emit('update:filter', 'priority', value)"
          >
            <template #trigger="{ option }">
              <span class="text-sm" :style="{ color: option?.color || 'var(--muted-foreground)' }">
                {{ option?.label || 'All Priorities' }}
              </span>
            </template>
          </TaskOptionMenu>
        </div>

        <div>
          <label class="mb-1 block text-xs text-muted-foreground">Assignee</label>
          <SearchableSelectMenu
            :model-value="filters.assigneeId"
            :options="assigneeOptions"
            placeholder="Assignee"
            search-placeholder="Search assignees..."
            allow-clear
            clear-label="All Assignees"
            storage-key="task-filter-assignee"
            empty-state-label="Search to find an assignee."
            variant="form"
            @update:model-value="value => emit('update:filter', 'assigneeId', String(value || ''))"
          />
        </div>

        <div>
          <label class="mb-1 block text-xs text-muted-foreground">Project</label>
          <SearchableSelectMenu
            :model-value="filters.projectId"
            :options="projectOptions"
            placeholder="Project"
            search-placeholder="Search projects..."
            allow-clear
            clear-label="All Projects"
            storage-key="task-filter-project"
            empty-state-label="Search to find a project."
            variant="form"
            @update:model-value="value => emit('update:filter', 'projectId', String(value || ''))"
          />
        </div>

        <div>
          <label class="mb-1 block text-xs text-muted-foreground">Client</label>
          <SearchableSelectMenu
            :model-value="filters.clientId"
            :options="clientOptions"
            placeholder="Client"
            search-placeholder="Search clients..."
            allow-clear
            clear-label="All clients"
            storage-key="task-filter-client"
            empty-state-label="Search to find a client."
            variant="form"
            @update:model-value="value => emit('update:filter', 'clientId', String(value || ''))"
          />
        </div>

        <div>
          <label class="mb-1 block text-xs text-muted-foreground">Tag</label>
          <SearchableSelectMenu
            :model-value="filters.tag"
            :options="tagOptions"
            placeholder="Tag"
            search-placeholder="Search tags..."
            allow-clear
            clear-label="All tags"
            show-all-by-default
            storage-key="task-filter-tag"
            empty-state-label="No tags have been created yet."
            variant="form"
            @update:model-value="value => emit('update:filter', 'tag', String(value || ''))"
          />
        </div>
      </div>
    </div>
  </div>
</template>
